import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StandardAppLayout } from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import {
    asEngineViolations,
    engineErrorMessage,
    type EngineError,
    type SchemaViolation,
} from 'src/shared/state/api-slices/engineErrorMessage';
import { useCreateStacItemMutation, type StacItem } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import ItemForm from './components/item-form/ItemForm';
import DocumentModes from './components/document-editor/DocumentModes';
import { useJsonHatch } from './components/document-editor/useJsonHatch';
import { emptyItemForm, formFromItem, toItemDocument } from './domain/documents/stacItemDocument';

/**
 * Publishing a version into a dataset.
 *
 * `POST` conflicts rather than replacing (ADR-016 decision 4): the catalogue keeps no
 * item history, so a silent overwrite would destroy the record of what we were
 * serving. Correcting a published version is an edit, and says so.
 */
const StacItemCreate = () => {
    const { collectionId = '' } = useParams<{ collectionId: string }>();
    const navigate = useNavigate();
    const [createItem, { isLoading }] = useCreateStacItemMutation();
    const [submitError, setSubmitError] = useState<string | undefined>();
    /** Where the engine says the document is wrong, so the form can mark those inputs. */
    const [submitViolations, setSubmitViolations] = useState<SchemaViolation[]>([]);
    const { values, setValues, baseline, generation, apply } = useJsonHatch(emptyItemForm(), document =>
        formFromItem(document as StacItem)
    );

    const publish = async (item: StacItem) => {
        setSubmitError(undefined);
        setSubmitViolations([]);

        try {
            await createItem({ collectionId, item }).unwrap();
            navigate(`/admin/catalogue/${collectionId}`);
        } catch (error) {
            setSubmitError(engineErrorMessage(error as EngineError));
            setSubmitViolations(asEngineViolations(error as EngineError));
        }
    };

    const cancel = () => navigate(`/admin/catalogue/${collectionId}`);

    return (
        <StandardAppLayout
            title='Publish a version'
            mainContent={
                <DocumentModes
                    noun='version'
                    buildDocument={() => toItemDocument(values, collectionId, baseline as StacItem | undefined)}
                    onApply={apply}
                >
                    <ItemForm
                        key={generation}
                        mode='create'
                        initialValues={values}
                        isSaving={isLoading}
                        submitError={submitError}
                        submitViolations={submitViolations}
                        onValuesChange={setValues}
                        onSubmit={next => publish(toItemDocument(next, collectionId, baseline as StacItem | undefined))}
                        onCancel={cancel}
                    />
                </DocumentModes>
            }
        />
    );
};

export default StacItemCreate;
